import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { DeviceType } from '~/types/device-type'

import Button from '~/components/Buttons/Button'
import InputGroup from '~/components/Inputs/InputGroup'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { useCreateAppExternalLinkMutation, useUpdateAppExternalLinkMutation } from '~/queries/external-links'

interface FormValues {
  name: string
  url: string
}

interface FormProps {
  appKey: string
  deviceType: DeviceType
  externalLinkID: string
  initialValues?: FormValues
  onClose: VoidFunction
  onFailure: (e: unknown) => void
  onSuccess: VoidFunction
}

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  url: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

const ManageExternalLinkForm = ({
  appKey,
  deviceType,
  externalLinkID,
  initialValues,
  onClose,
  onFailure,
  onSuccess,
}: FormProps): ReactElement => {
  /** Local state */

  const defaultValues: FormValues = {
    name: initialValues?.name || '',
    url: initialValues?.url || '',
  }

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  /** Queries & Mutations */

  const { mutateAsync: createExternalLinkMutation } = useCreateAppExternalLinkMutation()

  const { mutateAsync: updateExternalLinkMutation } = useUpdateAppExternalLinkMutation()

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    const { name, url } = values

    try {
      initialValues
        ? await updateExternalLinkMutation({
            appKey,
            deviceType,
            externalLinkID,
            target: name.trim(),
            url: url.trim(),
          })
        : await createExternalLinkMutation({ appKey, deviceType, target: name, url })
      onSuccess()
    } catch (e) {
      onFailure(e)
    }
  }

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <InputGroup>
          <TextInput
            {...register('name')}
            autoFocus
            disabled={!!initialValues}
            error={errors.name?.message}
            label="Name"
            placeholder="Enter name"
            required
          />
          <TextInput {...register('url')} error={errors.url?.message} label="Url" placeholder="Enter url" required />
        </InputGroup>
      </ModalBody>
      <ModalFooter>
        <Button disabled={isSubmitting} ghost onClick={onClose} type="button">
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Loading...' : 'Save'}
        </Button>
      </ModalFooter>
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const ManageExternalLinkModal = ({ initialValues, isOpen, onClose, ...props }: ModalProps): ReactElement => {
  return (
    <Modal className="manage-external-link-modal" isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>{initialValues ? 'Edit external link' : 'Create an external link'}</ModalHeader>
      <ManageExternalLinkForm {...props} initialValues={initialValues} onClose={onClose} />
    </Modal>
  )
}

export default ManageExternalLinkModal
